import React, { PropsWithChildren, useRef } from "react";
import {
  BackgroundColor,
  SectionContainer
} from "@silversea-ssc/ui-components-react";
import { PageBuilderCustomOfferBoxSectionContainer } from "./PageBuilderCustomOfferBox";

import {
  PageBuilderSectionsFragment,
  PageType,
  SectionLayoutConfigFragment
} from "types/graphql-types";
import { hasContent, PageBuilderAutoHeading } from "./pageBuilderRenderHelper";
import { PageTypeContext } from "./usePageType";
import { match } from "ts-pattern";
import { dequal } from "dequal/lite";
import {
  backgroundColorForSection,
  colorFromConfiguration
} from "./backgroundColor";
import PageBuilderEmotionalContentSlider from "components/PageBuilder/PageBuilderEmotionalContentSlider";

const PageBuilderOpeningText = React.lazy(
  () => import("./PageBuilderOpeningText")
);

const PageBuilderOpeningHeroVideo = React.lazy(
  () => import("./PageBuilderOpeningHeroVideo")
);

const PageBuilderDescriptionText = React.lazy(
  () => import("./PageBuilderDescriptionText")
);
const PageBuilderOpeningHero = React.lazy(
  () => import("./PageBuilderOpeningHero")
);
const PageBuilderCollectionMosaic = React.lazy(
  () => import("./PageBuilderCollectionMosaic")
);
const PageBuilderPageSlider = React.lazy(
  () => import("./PageBuilderPageSlider")
);
const PageBuilderCollectionSlider = React.lazy(
  () => import("./PageBuilderCollectionSlider")
);
const PageBuilderPageMosaic = React.lazy(
  () => import("./PageBuilderPageMosaic")
);
const PageBuilderServiceRows = React.lazy(
  () => import("./PageBuilderServiceRows")
);
const PageBuilderTextBox = React.lazy(() => import("./PageBuilderTextBox"));
const PageBuilderFeaturedContentRows = React.lazy(
  () => import("./PageBuilderFeaturedContentRows")
);
const PageBuilderVideoSection = React.lazy(
  () => import("./PageBuilderVideoSection")
);
const PageBuilderFyc = React.lazy(() => import("./fyc/PageBuilderFyc"));
const PageBuilderDividerWithCTA = React.lazy(
  () => import("./PageBuilderDividerWithCTA")
);

const PageBuilderCruiseSlider = React.lazy(
  () => import("./fyc/PageBuilderCruiseSlider")
);
const PageBuilderOfferDescriptionRow = React.lazy(
  () => import("./PageBuilderOfferDescriptionRow")
);

const PageBuilderOpeningHeroSlider = React.lazy(
  () => import(/* webpackPreload: true */ "./PageBuilderOpeningHeroSlider")
);

const PageBuilderCardMosaic = React.lazy(
  () => import("./PageBuilderCardMosaic/PageBuilderCardMosaic")
);
const PageBuilderOffersSelectionSlider = React.lazy(
  () => import("./PageBuilderOffersSelectionSlider")
);

const PageBuilderAccordionText = React.lazy(
  () => import("./PageBuilderAccordionText")
);

const PageBuilderCustomComponentContainer = React.lazy(
  () => import("./customComponents/CustomComponentContainer")
);

const PageBuilderTeaWithSilverseaRegistration = React.lazy(
  () => import("./PageBuilderTeaWithSilverseaRegistration")
);

const PageBuilderSharedSectionRef = React.lazy(
  () => import("./PageBuilderSharedSectionRef")
);

const PageBuilderBlogMosaic = React.lazy(
  () => import("./PageBuilderBlogMosaic")
);
const PageBuilderFaresMosaic = React.lazy(
  () => import("./PageBuilderFaresMosaic")
);
const PageBuilderCardSlider = React.lazy(
  () => import("./PageBuilderCardSlider/PageBuilderCardSlider")
);

const PageBuilderStickyNav = React.lazy(() => import("./PageBuilderStickyNav"));
const PageBuilderBrochureLeadForm = React.lazy(
  () => import("./PageBuilderBrochureLeadForm")
);
const PageBuilderInlineCTABox = React.lazy(
  () => import("./PageBuilderInlineCTABox")
);

const PageBuilderLocationSpecificTextBoxes = React.lazy(
  () => import("./PageBuilderLocationSpecificTextBoxes")
);
const PageBuilderSubscribeToNewsletter = React.lazy(
  () => import("./PageBuilderSubscribeToNewsletter")
);

const PageBuilderEmotionalLauncher = React.lazy(
  () => import("./PageBuilderEmotionalLauncher")
);

const PageBuilderSectionSlider = React.lazy(
  () => import("./PageBuilderSectionSlider")
);
const PageBuilderMilestones = React.lazy(
  () => import("./PageBuilderMilestones")
);

const PageBuilderLastSeenProducts = React.lazy(
  () => import("./PageBuilderLastSeenProducts")
);

const PageBuilderInlineLeadForm = React.lazy(
  () => import("./PageBuilderInlineLeadForm")
);

const PageBuilderExpeditionTeamSection = React.lazy(
  () => import("./PageBuilderExpeditionTeamSection")
);

export type SanityBuilderSectionType = PageBuilderSectionsFragment;

interface SanityBuilderToReactProps {
  sections: SanityBuilderSectionType[];
  initialBackgroundColor?: BackgroundColor;
  pageType?: PageType;
}

const isStickyComponent = (section: PageBuilderSectionsFragment): boolean => {
  return match(section)
    .with({ __typename: "SanityStickyNav" }, () => true)
    .with({ __typename: "SanityBrochureLeadForm", asSticky: true }, () => true)
    .otherwise(() => false);
};

const SanityBuilderToComponents: React.FC<
  React.PropsWithChildren<SanityBuilderToReactProps>
> = ({ sections, initialBackgroundColor = "white", pageType = "PAGE" }) => {
  const autoHeading = React.useMemo(() => new PageBuilderAutoHeading(), []);

  const configuredBackgroundColorForFirstSection = colorFromConfiguration(
    sections[0],
    initialBackgroundColor
  );
  const lastBackgroundColor = useRef(
    configuredBackgroundColorForFirstSection || initialBackgroundColor
  );

  return (
    <PageTypeContext.Provider value={pageType}>
      <div data-testid="container">
        {sections.map((section, index) => {
          const isLastSection = sections.length - 1 == index;
          const previousBackgroundColor = lastBackgroundColor.current;

          const sectionBackgroundColor =
            index == 0
              ? previousBackgroundColor
              : backgroundColorForSection(
                  section,
                  sections[index - 1].__typename,
                  previousBackgroundColor
                );

          //TODO can this be done inside backgroundColorForSection?
          if (sections[index].__typename !== "SanityDividerWithCTA")
            lastBackgroundColor.current = sectionBackgroundColor;

          if (isStickyComponent(section)) {
            return (
              <SuspendedInternalSanityBuilderSectionToComponent
                backgroundColor={sectionBackgroundColor}
                previousBackgroundColor={previousBackgroundColor}
                autoHeading={autoHeading}
                key={section ? section["_key"] || -1 : -1}
                section={section}
                data-page-builder-anchor={section["_key"]}
              />
            );
          }

          return (
            <React.Fragment key={section ? section["_key"] || index : index}>
              <div
                id={section["_key"]}
                data-page-builder-anchor={section["_key"]}
                data-content-name={`section_${index + 1}`}
              >
                <SuspendedInternalSanityBuilderSectionToComponent
                  backgroundColor={sectionBackgroundColor}
                  previousBackgroundColor={previousBackgroundColor}
                  autoHeading={autoHeading}
                  section={section}
                />
                {section.__typename == "SanityVideoSection" &&
                  isLastSection && (
                    // whitespace since video component and footer have the same bg color
                    <SectionContainer
                      backgroundColor="white"
                      spacingTop="condensed"
                    ></SectionContainer>
                  )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </PageTypeContext.Provider>
  );
};

type InternalSanityBuilderSectionToComponentProps = {
  backgroundColor: BackgroundColor;
  previousBackgroundColor?: BackgroundColor;
  autoHeading: PageBuilderAutoHeading;
  section: SanityBuilderSectionType;
};

const SuspendedInternalSanityBuilderSectionToComponent: React.FC<
  InternalSanityBuilderSectionToComponentProps
> = (props) => (
  <React.Suspense>
    <InternalSanityBuilderSectionToComponent {...props} />
  </React.Suspense>
);

const InternalSanityBuilderSectionToComponent: React.FC<
  InternalSanityBuilderSectionToComponentProps
> = ({ section, backgroundColor, previousBackgroundColor, autoHeading }) =>
  match(section)
    .with({ __typename: "SanityAccordionText" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="AccordionText_section"
      >
        <PageBuilderAccordionText {...s} backgroundColor={backgroundColor} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityBlogMosaic" }, (s) => (
      <DefaultSectionContainer
        backgroundColor={backgroundColor}
        dataContentName="card_mosaic"
      >
        <PageBuilderBlogMosaic {...s} backgroundColor={backgroundColor} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityBrochureLeadForm" }, (s) => (
      <PageBuilderBrochureLeadForm {...s} backgroundColor={backgroundColor} />
    ))
    .with({ __typename: "SanityCardMosaic" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="card_mosaic"
      >
        <PageBuilderCardMosaic {...s} backgroundColor={backgroundColor} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityCardSlider" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="card_slider"
      >
        <PageBuilderCardSlider {...s} backgroundColor={backgroundColor} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityCollectionMosaic" }, (s) => {
      const { collectionData } = s;

      return (
        <DefaultSectionContainer
          sectionLayoutConfig={s.sectionLayoutConfig}
          backgroundColor={backgroundColor}
          dataContentName="collection_mosaic"
        >
          <PageBuilderCollectionMosaic
            {...s}
            collectionData={collectionData}
            backgroundColor={backgroundColor}
          />
        </DefaultSectionContainer>
      );
    })
    .with({ __typename: "SanityCollectionSlider" }, (s) => {
      const { collectionData } = s;

      return (
        <DefaultSectionContainer
          sectionLayoutConfig={s.sectionLayoutConfig}
          backgroundColor={backgroundColor}
          dataContentName="collection_slider"
        >
          <PageBuilderCollectionSlider
            {...s}
            collectionData={collectionData}
            backgroundColor={backgroundColor}
          />
        </DefaultSectionContainer>
      );
    })
    .with({ __typename: "SanityCruiseSlider" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="cruise_slider"
      >
        <PageBuilderCruiseSlider {...s} backgroundColor={backgroundColor} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityCustomComponent" }, (s) => (
      <PageBuilderCustomComponentContainer
        backgroundColor={backgroundColor}
        section={s}
      />
    ))
    .with({ __typename: "SanityCustomOfferBox" }, (s) => (
      <PageBuilderCustomOfferBoxSectionContainer
        backgroundColor={backgroundColor}
        section={s}
      />
    ))
    .with({ __typename: "SanityDescriptionText" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="description_text"
      >
        <PageBuilderDescriptionText
          headingType={autoHeading.getSingleUseHeadingType(
            s?.content?.leadIn?.localized,
            "H1",
            "H2"
          )}
          {...s}
          backgroundColor={backgroundColor}
        />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityDividerWithCTA" }, (s) => (
      <PageBuilderDividerWithCTA
        {...s}
        backgroundColorTop={previousBackgroundColor}
        backgroundColorBottom={backgroundColor}
      />
    ))
    .with({ __typename: "SanityFaresMosaic" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="fares_mosaic"
      >
        <PageBuilderFaresMosaic {...s} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityFeaturedContentRows" }, (s) => (
      <SectionContainer
        spacingTop={s.heading ? "regular" : "compact"}
        spacingBottom={s.heading ? "regular" : "compact"}
        backgroundColor={backgroundColor}
        dataContentName="featured_content_rows"
      >
        <PageBuilderFeaturedContentRows
          headingType={autoHeading.getSingleUseHeadingType(
            s?.heading?.title?.localized,
            "H1",
            "H2"
          )}
          {...s}
        />
      </SectionContainer>
    ))
    .with({ __typename: "SanityFyc" }, (s) => <PageBuilderFyc {...s} />)
    .with({ __typename: "SanityInlineCTABox" }, (s) => (
      <SectionContainer
        backgroundColor={backgroundColor}
        dataContentName="inline-cta-box"
        spacingTop="condensed"
        spacingBottom="condensed"
      >
        <PageBuilderInlineCTABox {...s} />
      </SectionContainer>
    ))
    .with({ __typename: "SanityLocationSpecificTextBoxes" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="text_box"
      >
        <PageBuilderLocationSpecificTextBoxes {...s} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityOfferDescriptionRow" }, (s) => (
      <SectionContainer
        spacingTop="compact"
        spacingBottom="compact"
        backgroundColor={backgroundColor}
        dataContentName="OfferDescriptionRow_section"
      >
        <PageBuilderOfferDescriptionRow
          {...s}
          backgroundColor={backgroundColor}
        />
      </SectionContainer>
    ))
    .with({ __typename: "SanityOffersSelectionMosaic" }, () => null)
    .with({ __typename: "SanityOffersSelectionSlider" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="collection_slider"
      >
        <PageBuilderOffersSelectionSlider
          {...s}
          backgroundColor={backgroundColor}
        />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityOpeningHero" }, (s) => {
      if (s.title?.localized?.trim()?.length || 0 > 0) {
        autoHeading.markHeadingUsed("H1");
      }
      return <PageBuilderOpeningHero {...s} />;
    })
    .with({ __typename: "SanityOpeningHeroVideo" }, (s) => {
      if (s.title?.localized?.trim()?.length || 0 > 0) {
        autoHeading.markHeadingUsed("H1");
      }
      return <PageBuilderOpeningHeroVideo {...s} />;
    })
    .with({ __typename: "SanityOpeningHeroSlider" }, (s) => (
      <PageBuilderOpeningHeroSlider {...s} />
    ))
    .with({ __typename: "SanityOpeningText" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="opening_text"
      >
        <PageBuilderOpeningText {...s} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityPageMosaic" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="page_mosaic"
      >
        <PageBuilderPageMosaic {...s} backgroundColor={backgroundColor} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityPageSlider" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="page_slider"
      >
        <PageBuilderPageSlider {...s} backgroundColor={backgroundColor} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanitySectionSlider" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="section_slider"
      >
        <PageBuilderSectionSlider {...s} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityServiceRows" }, (s) => (
      <div id={s.anchor}>
        <SectionContainer
          spacingTop={s.heading ? "regular" : "compact"}
          spacingBottom={s.heading ? "regular" : "compact"}
          backgroundColor={backgroundColor}
          dataContentName="featured_content_rows"
        >
          <PageBuilderServiceRows {...s} backgroundColor={backgroundColor} />
        </SectionContainer>
      </div>
    ))
    .with({ __typename: "SanitySharedSectionRef" }, (s) => (
      <PageBuilderSharedSectionRef
        {...s}
        initialBackgroundColor={backgroundColor || "white"}
      />
    ))
    .with({ __typename: "SanityStickyNav" }, (s) => (
      <PageBuilderStickyNav {...s} />
    ))
    .with({ __typename: "SanitySubscribeToNewsletter" }, (s) => (
      <PageBuilderSubscribeToNewsletter {...s} />
    ))
    .with({ __typename: "SanityTeaWithSilverseaRegistration" }, (s) => (
      <PageBuilderTeaWithSilverseaRegistration {...s} />
    ))
    .with({ __typename: "SanityTextBox" }, (s) => {
      return (
        <DefaultSectionContainer
          sectionLayoutConfig={s.sectionLayoutConfig}
          backgroundColor={backgroundColor}
          dataContentName="text_box"
        >
          <PageBuilderTextBox {...s} />
        </DefaultSectionContainer>
      );
    })
    .with({ __typename: "SanityVideoSection" }, (s) => (
      <DefaultSectionContainer
        backgroundColor={backgroundColor}
        dataContentName="Video_section"
      >
        <PageBuilderVideoSection {...s} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityMilestones" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="milestones"
      >
        <PageBuilderMilestones {...s} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityLastSeenProducts" }, (s) => (
      <PageBuilderLastSeenProducts backgroundColor={backgroundColor} {...s} />
    ))
    .with({ __typename: "SanityInlineLeadForm" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="inline_lead_form"
      >
        <PageBuilderInlineLeadForm backgroundColor={backgroundColor} {...s} />
      </DefaultSectionContainer>
    ))
    .with({ __typename: "SanityEmotionalLauncher" }, (s) => (
      <PageBuilderEmotionalLauncher {...s} />
    ))
    .with({ __typename: "SanityExpeditionTeamSection" }, (s) => (
      <PageBuilderExpeditionTeamSection {...s} />
    ))
    .with({ __typename: "SanityEmotionalContentSlider" }, (s) => (
      <DefaultSectionContainer
        sectionLayoutConfig={s.sectionLayoutConfig}
        backgroundColor={backgroundColor}
        dataContentName="card_slider"
      >
        <PageBuilderEmotionalContentSlider
          {...s}
          backgroundColor={backgroundColor}
        />
      </DefaultSectionContainer>
    ))
    .exhaustive();

const DefaultSectionContainer: React.FC<
  PropsWithChildren<{
    sectionLayoutConfig?: SectionLayoutConfigFragment;
    dataContentName: string;
    backgroundColor: BackgroundColor;
  }>
> = ({ sectionLayoutConfig, children, backgroundColor, dataContentName }) => {
  const customSpacingTop = sectionLayoutConfig?.spacingTop || "regular";
  const customSpacingBottom = sectionLayoutConfig?.spacingBottom || "regular";

  return (
    <SectionContainer
      spacingTop={customSpacingTop == "none" ? null : customSpacingTop}
      spacingBottom={customSpacingBottom == "none" ? null : customSpacingBottom}
      backgroundColor={backgroundColor}
      dataContentName={dataContentName}
    >
      {children}
    </SectionContainer>
  );
};

export { hasContent };
export default React.memo(SanityBuilderToComponents, dequal);
